import React, { useMemo, useState, useCallback } from 'react';
import { Box, Collapse } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import SchedulesHeader from '../components/Schedules/SchedulesHeader';
import ServicesToSchedule from '../components/Schedules/ServicesToSchedule';
import ScheduledServices from '../components/Schedules/ScheduledServices';
import SchedulesStatusDashboard, {
  type StatusDashboardData,
} from '../components/Schedules/SchedulesStatusDashboard';
import SchedulesVehiclesDriversPanel from '../components/Schedules/SchedulesVehiclesDriversPanel';
import SchedulesFloatingActions from '../components/Schedules/SchedulesFloatingActions';
import DriversManagementDrawer from '../components/Schedules/DriversManagementDrawer';
import VehiclesManagementDrawer from '../components/Schedules/VehiclesManagementDrawer';
import VehicleManagementDrawer from '../components/Schedules/VehicleManagementDrawer';
import servicesMock from '../data/mockedData/schedules/services.json';
import scheduledServicesMock from '../data/mockedData/schedules/scheduledServices.json';
import statusDashboardMock from '../data/mockedData/schedules/statusDashboard.json';
import { useModal } from '../store/ModalProvider';
import type { SendScheduleModalData, ScheduleTasksModalData } from '../types/modal';

interface ServiceSummary {
  id: number;
  startDate: string;
  startTime: string;
  service: string;
  startLocation: string;
  endLocation: string;
  endTime: string;
  endDate: string;
  client: string;
  observations: string;
  driver: string;
  vehicle: string;
}

const SchedulesPage = React.memo(function SchedulesPage() {
  const { openModal } = useModal();
  const [statusVisible, setStatusVisible] = useState(false);
  const [resourcesCollapsed, setResourcesCollapsed] = useState(false);
  const [resourcesContentVisible, setResourcesContentVisible] =
    useState(true);
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  const [manageDriversDrawerOpen, setManageDriversDrawerOpen] =
    useState(false);
  const [manageVehiclesDrawerOpen, setManageVehiclesDrawerOpen] =
    useState(false);
  const [scheduledSelection, setScheduledSelection] =
    useState<GridRowSelectionModel>([]);
  const [compatibilityAlert, setCompatibilityAlert] = useState<string | null>(
    null,
  );

  const services = useMemo(() => servicesMock as ServiceSummary[], []);
  const scheduledServices = useMemo(
    () => scheduledServicesMock as ServiceSummary[],
    [],
  );
  const selectedScheduledServices = useMemo(
    () =>
      scheduledServices.filter((service) =>
        scheduledSelection.includes(service.id),
      ),
    [scheduledServices, scheduledSelection],
  );
  const hasScheduledSelection = scheduledSelection.length > 0;
  const scheduleTasks = useMemo<ScheduleTasksModalData['tasks']>(
    () => [
      {
        id: 'task-4203-1',
        code: '4203',
        title: 'Ausente por motivo de consulta no dia 02/05/2024 - 15:00 as 16:00.',
        description:
          'Ausente por motivo de consulta no dia 02/05/2024 - 15:00 as 16:00.',
        status: 'pending',
        startDate: '02/05/2024 - 08:00',
        endDate: '03/05/2024 - 08:00',
        impedeEscalar: false,
        estado: 'Nao Concluida',
        descricao:
          'Ausente por motivo de consulta no dia 02/05/2024 - 15:00 as 16:00.',
        nome: 'Gabriel Santos',
        nr: '0137',
        nrM: '0137',
      },
      {
        id: 'task-4203-2',
        code: '4203',
        title: 'Ausente das 15h as 16h.',
        description: 'Ausente das 15h as 16h.',
        status: 'completed',
        startDate: '02/05/2024 - 08:00',
        endDate: '03/05/2024 - 08:00',
        impedeEscalar: true,
        estado: 'Concluida',
        descricao: 'Ausente das 15h as 16h.',
        nome: 'Rita Sofia',
        nr: '0137',
        nrM: '0137',
      },
      {
        id: 'task-4202',
        code: '4202',
        title: 'Precisa tirar o dia todo.',
        description: 'Precisa tirar o dia todo.',
        status: 'pending',
        startDate: '02/05/2024 - 08:00',
        endDate: '03/05/2024 - 08:00',
        impedeEscalar: true,
        estado: 'Nao Concluida',
        descricao: 'Precisa tirar o dia todo.',
        nome: 'Jorge Rodrigues',
        nr: '0137',
        nrM: '0137',
      },
      {
        id: 'task-4201',
        code: '4201',
        title: 'Consulta durante a manha.',
        description: 'Consulta durante a manha.',
        status: 'pending',
        startDate: '02/05/2024 - 08:00',
        endDate: '03/05/2024 - 08:00',
        impedeEscalar: false,
        estado: 'Nao Concluida',
        descricao: 'Consulta durante a manha.',
        nome: 'Carla Silva',
        nr: '0137',
        nrM: '0137',
      },
      {
        id: 'task-4200',
        code: '4200',
        title: 'Precisa tirar a tarde.',
        description: 'Precisa tirar a tarde.',
        status: 'completed',
        startDate: '01/05/2024 - 08:00',
        endDate: '02/05/2024 - 08:00',
        impedeEscalar: false,
        estado: 'Concluida',
        descricao: 'Precisa tirar a tarde.',
        nome: 'Joao Gomes',
        nr: '0137',
        nrM: '0137',
      },
    ],
    [],
  );
  const statusDashboard = useMemo(
    () => statusDashboardMock as StatusDashboardData,
    [],
  );

  const handleToggleResources = () => {
    if (!resourcesContentVisible) {
      setResourcesCollapsed(false);
      requestAnimationFrame(() => {
        setResourcesContentVisible(true);
      });
      return;
    }
    setResourcesContentVisible(false);
  };

  const handleResourcesContentTransitionEnd = () => {
    if (!resourcesContentVisible) {
      setResourcesCollapsed(true);
    }
  };

  const handleOpenManageDrawer = () => {
    setManageDrawerOpen(true);
  };

  const handleCloseManageDrawer = () => {
    setManageDrawerOpen(false);
  };

  const handleOpenManageDriversDrawer = () => {
    setManageVehiclesDrawerOpen(false);
    setManageDriversDrawerOpen(true);
  };

  const handleCloseManageDriversDrawer = () => {
    setManageDriversDrawerOpen(false);
  };

  const handleOpenManageVehiclesDrawer = () => {
    setManageDriversDrawerOpen(false);
    setManageVehiclesDrawerOpen(true);
  };

  const handleCloseManageVehiclesDrawer = () => {
    setManageVehiclesDrawerOpen(false);
  };

  const handleScheduledSelectionChange = useCallback(
    (nextSelection: GridRowSelectionModel) => {
      setScheduledSelection(nextSelection);
      if (compatibilityAlert) {
        setCompatibilityAlert(null);
      }
    },
    [compatibilityAlert],
  );

  const handleAssignDriver = useCallback(
    (driverId: number) => {
      if (!hasScheduledSelection) {
        return;
      }
      const hasConflict = selectedScheduledServices.some(
        (service) => service.driver && service.driver.trim() !== '',
      );
      if (hasConflict) {
        setCompatibilityAlert(
          'Alguns servicos selecionados ja tem motorista atribuido.',
        );
        return;
      }
      console.log('Atribuir motorista:', driverId, selectedScheduledServices);
    },
    [hasScheduledSelection, selectedScheduledServices],
  );

  const handleAssignVehicle = useCallback(
    (vehicleId: number) => {
      if (!hasScheduledSelection) {
        return;
      }
      const hasConflict = selectedScheduledServices.some(
        (service) => service.vehicle && service.vehicle.trim() !== '',
      );
      if (hasConflict) {
        setCompatibilityAlert(
          'Alguns servicos selecionados ja tem viatura atribuida.',
        );
        return;
      }
      console.log('Atribuir viatura:', vehicleId, selectedScheduledServices);
    },
    [hasScheduledSelection, selectedScheduledServices],
  );

  const handleCloseCompatibilityAlert = useCallback(() => {
    setCompatibilityAlert(null);
  }, []);

  const handleOpenScheduleTasks = () => {
    const tasksData: ScheduleTasksModalData = {
      tasks: scheduleTasks,
      date: '01/05/2024',
      centroId: 'centro-1',
    };

    openModal('Tarefas da Escala', tasksData, []);
  };

  const handleOpenSendSchedule = () => {
    const sendScheduleData: SendScheduleModalData = {
      scheduledServices:
        scheduledServicesMock as SendScheduleModalData['scheduledServices'],
      date: '01/05/2024',
      centroId: 'centro-1',
    };

    const handlePrint = () => {
      console.log('Imprimir escala', sendScheduleData);
    };

    const handleSend = () => {
      console.log('Enviar escala', sendScheduleData);
    };

    openModal('Enviar Escala', sendScheduleData, [handlePrint, handleSend]);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexGrow: 1,
        width: '100%',
        height: '100%',
      }}
    >
      <SchedulesHeader
        onStatusChange={setStatusVisible}
        onManageScheduleClick={handleOpenManageDrawer}
      />
      <Collapse in={statusVisible} timeout={280} unmountOnExit>
        <Box sx={{ marginTop: '16px' }}>
          <SchedulesStatusDashboard data={statusDashboard} />
        </Box>
      </Collapse>
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          marginTop: '24px',
          display: 'grid',
          transition: 'grid-template-columns 0.35s ease-in-out',
          gridTemplateColumns: resourcesCollapsed
            ? 'minmax(0, 1fr) 40px'
            : 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: '24px',
          position: 'relative',
          paddingBottom: '96px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            minWidth: 0,
          }}
        >
          <ServicesToSchedule />
          <ScheduledServices
            rows={scheduledServices}
            selectedRowIds={scheduledSelection}
            onSelectionChange={handleScheduledSelectionChange}
            compatibilityAlert={compatibilityAlert ?? undefined}
            onCloseCompatibilityAlert={handleCloseCompatibilityAlert}
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            width: '100%',
            minHeight: 0,
          }}
        >
          <SchedulesVehiclesDriversPanel
            collapsed={resourcesCollapsed}
            contentVisible={resourcesContentVisible}
            onToggle={handleToggleResources}
            onContentTransitionEnd={handleResourcesContentTransitionEnd}
            onManageDriversClick={handleOpenManageDriversDrawer}
            onManageVehiclesClick={handleOpenManageVehiclesDrawer}
            canAssign={hasScheduledSelection}
            onAssignDriver={handleAssignDriver}
            onAssignVehicle={handleAssignVehicle}
          />
        </Box>
        <SchedulesFloatingActions
          tasksCount={scheduleTasks.length}
          onScheduleTasks={handleOpenScheduleTasks}
          onSendSchedule={handleOpenSendSchedule}
          onSendScheduleUpcoming={handleOpenSendSchedule}
          onPrintSchedule={handleOpenSendSchedule}
        />
      </Box>
      <VehicleManagementDrawer
        open={manageDrawerOpen}
        onClose={handleCloseManageDrawer}
      />
      <DriversManagementDrawer
        open={manageDriversDrawerOpen}
        onClose={handleCloseManageDriversDrawer}
      />
      <VehiclesManagementDrawer
        open={manageVehiclesDrawerOpen}
        onClose={handleCloseManageVehiclesDrawer}
      />
    </Box>
  );
});

export default SchedulesPage;

